import "./style.css";
import { baseScenarioState, createScenarioState } from "./sim-core.js";
import { createSceneView } from "./scene-view.js";
import { buildEtopoSurfaceFromAoi, parseAoiRingFromGeoJson } from "./etopo-surface.js";
import { buildTinSpatialBins, getTerrainExtents, getTerrainType } from "./terrain-data.js";
import { clearTerrainData, getTerrainData, sampleSeafloorHeight, sampleSeafloorHeightWithDataStrict, setTerrainData } from "./terrain.js";
import { createUi } from "./ui.js";

const FULLSCREEN_SIM_HANDOFF_KEY = "hydrotools:fullscreen-handoff:simulator:v1";

function consumeFullscreenHandoffIntent() {
  try {
    const raw = sessionStorage.getItem(FULLSCREEN_SIM_HANDOFF_KEY);
    if (!raw) return false;
    sessionStorage.removeItem(FULLSCREEN_SIM_HANDOFF_KEY);
    const parsed = JSON.parse(raw);
    const ts = Number(parsed?.ts || 0);
    if (!Number.isFinite(ts)) return false;
    return (Date.now() - ts) < 45_000;
  } catch {
    return false;
  }
}

function setupFullscreenRestoreHandoff() {
  if (!consumeFullscreenHandoffIntent()) return;
  if (!document.fullscreenEnabled || document.fullscreenElement) return;
  const restore = async () => {
    try { await document.documentElement.requestFullscreen(); } catch {}
  };
  const isInteractiveTarget = (target) => !!target?.closest?.("a,button,input,select,textarea,label,summary,.sim-panel,.sim-footer,.control-group,.icon-btn");
  const onPointer = (e) => {
    if (isInteractiveTarget(e.target)) return;
    cleanup();
    restore();
  };
  const onKey = () => {
    const active = document.activeElement;
    if (active && (active.matches?.("a,button,input,select,textarea,summary") || active.closest?.(".sim-panel,.sim-footer"))) return;
    cleanup();
    restore();
  };
  const cleanup = () => {
    window.removeEventListener("pointerdown", onPointer, true);
    window.removeEventListener("keydown", onKey, true);
  };
  window.addEventListener("pointerdown", onPointer, true);
  window.addEventListener("keydown", onKey, true);
}

function setupSimulatorFullscreenButton() {
  const btn = document.getElementById("sim-fullscreen-btn");
  if (!btn) return;
  const sync = () => {
    btn.textContent = document.fullscreenElement ? "Exit Fullscreen" : "Fullscreen";
  };
  const toggle = async () => {
    try {
      if (!document.fullscreenEnabled) return;
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
      sync();
    } catch {}
  };
  btn.addEventListener("click", toggle);
  document.addEventListener("fullscreenchange", sync);
  sync();
}

setupFullscreenRestoreHandoff();
setupSimulatorFullscreenButton();

const stateA = createScenarioState();

const sceneAHost = document.getElementById("scene-host-a");
const viewA = createSceneView({
  host: sceneAHost,
  onCameraChange: () => {},
});

const simWorker = new Worker(new URL("./sim-worker.js", import.meta.url), { type: "module" });
const densityWorker = new Worker(new URL("./density-worker.js", import.meta.url), { type: "module" });
let latestSnapshot = null;
const pendingSnapshots = [];
const frameTimes = [];
let lastFrameTime = 0;
let perfBudgetKey = "";
let workerLoopHz = 0;
let playbackRunning = false;
let sonarRecording = false;
let wreckWorkerMeshKey = "";
let lastMissionPlanEnabled = Boolean(stateA.missionPlanEnabled);
let lastMissionSkipTurns = Boolean(stateA.missionSkipTurns);
let presetLocked = false;
let loadedAoiRing = null;
const missionPlanState = {
  segments: [],
  waypoints: [],
  version: 0,
  spacing: 0,
  skipTurns: false,
};
const presets = {
  default: {
    cameraMode: "free",
    missionLockAoi: true,
    accuracyMode: "fast",
    svpMode: "constant",
    frequency: 300,
    pulseWidth: 2,
    soundVelocity: 1500,
    svSurfaceVelocity: 1505,
    svDeepVelocity: 1485,
    svTransitionDepth: 60,
    range: 80,
    speed: 4,
    pingRate: 10,
    rawStripUpdateRate: 10,
    simTimeScale: 1,
    historyLength: 300,
    historyUnlimited: false,
    beamCount: 256,
    beamSpacingMode: "equiangular",
    beamAngle: 120,
    rollEnabled: true,
    rollStabilizationEnabled: true,
    rollStabilizationStrength: 90,
    roll: 0,
    rollAmplitude: 6,
    rollPeriod: 8,
    rollVariability: 0.25,
    pitch: 0,
    pitchAmplitude: 2,
    pitchPeriod: 6,
    pitchVariability: 0.2,
    pitchEnabled: true,
    pitchStabilizationEnabled: true,
    pitchStabilizationStrength: 90,
    yaw: 0,
    yawAmplitude: 3,
    yawPeriod: 10,
    yawVariability: 0.2,
    yawEnabled: true,
    yawStabilizationEnabled: true,
    yawStabilizationStrength: 90,
    course: 0,
    steerage: 0,
    missionPlanEnabled: false,
    missionPlanHeading: 0,
    missionPlanOverlap: 20,
    missionOptimizeHeading: true,
    missionSkipTurns: false,
    missionWaypoints: [],
    seafloorPreset: "flat",
  },
  shallow_high_res: {
    cameraMode: "free",
    missionLockAoi: true,
    accuracyMode: "full",
    svpMode: "constant",
    frequency: 550,
    pulseWidth: 0.8,
    soundVelocity: 1500,
    svSurfaceVelocity: 1505,
    svDeepVelocity: 1485,
    svTransitionDepth: 40,
    range: 35,
    speed: 5.8,
    pingRate: 14,
    rawStripUpdateRate: 14,
    simTimeScale: 1,
    historyLength: 450,
    historyUnlimited: false,
    beamCount: 256,
    beamSpacingMode: "equiangular",
    beamAngle: 110,
    rollEnabled: true,
    rollStabilizationEnabled: true,
    rollStabilizationStrength: 90,
    roll: 0,
    rollAmplitude: 4,
    rollPeriod: 7,
    rollVariability: 0.2,
    pitch: 0,
    pitchAmplitude: 1.5,
    pitchPeriod: 5.5,
    pitchVariability: 0.15,
    pitchEnabled: true,
    pitchStabilizationEnabled: true,
    pitchStabilizationStrength: 90,
    yaw: 0,
    yawAmplitude: 2,
    yawPeriod: 9,
    yawVariability: 0.15,
    yawEnabled: true,
    yawStabilizationEnabled: true,
    yawStabilizationStrength: 90,
    course: 0,
    steerage: 0,
    missionPlanEnabled: false,
    missionPlanHeading: 0,
    missionPlanOverlap: 20,
    missionOptimizeHeading: true,
    missionSkipTurns: false,
    missionWaypoints: [],
    seafloorPreset: "flat",
  },
  deep_tradeoff: {
    cameraMode: "free",
    missionLockAoi: true,
    accuracyMode: "fast",
    svpMode: "constant",
    frequency: 95,
    pulseWidth: 9,
    soundVelocity: 1490,
    svSurfaceVelocity: 1490,
    svDeepVelocity: 1480,
    svTransitionDepth: 110,
    range: 190,
    speed: 4,
    pingRate: 6,
    rawStripUpdateRate: 6,
    simTimeScale: 1,
    historyLength: 260,
    historyUnlimited: false,
    beamCount: 128,
    beamSpacingMode: "equiangular",
    beamAngle: 130,
    rollEnabled: true,
    rollStabilizationEnabled: true,
    rollStabilizationStrength: 90,
    roll: 1,
    rollAmplitude: 8,
    rollPeriod: 9,
    rollVariability: 0.45,
    pitch: 0,
    pitchAmplitude: 3,
    pitchPeriod: 7,
    pitchVariability: 0.35,
    pitchEnabled: true,
    pitchStabilizationEnabled: true,
    pitchStabilizationStrength: 90,
    yaw: 4,
    yawAmplitude: 4,
    yawPeriod: 12,
    yawVariability: 0.4,
    yawEnabled: true,
    yawStabilizationEnabled: true,
    yawStabilizationStrength: 90,
    course: 4,
    steerage: 0,
    missionPlanEnabled: false,
    missionPlanHeading: 0,
    missionPlanOverlap: 20,
    missionOptimizeHeading: true,
    missionSkipTurns: false,
    missionWaypoints: [],
    seafloorPreset: "slope",
  },
  sv_sensitivity: {
    cameraMode: "free",
    missionLockAoi: true,
    accuracyMode: "full",
    svpMode: "layered",
    frequency: 280,
    pulseWidth: 2.5,
    soundVelocity: 1465,
    svSurfaceVelocity: 1510,
    svDeepVelocity: 1470,
    svTransitionDepth: 90,
    range: 95,
    speed: 2.9,
    pingRate: 9,
    rawStripUpdateRate: 9,
    simTimeScale: 1,
    historyLength: 320,
    historyUnlimited: false,
    beamCount: 256,
    beamSpacingMode: "equiangular",
    beamAngle: 120,
    rollEnabled: true,
    rollStabilizationEnabled: true,
    rollStabilizationStrength: 90,
    roll: 0,
    rollAmplitude: 6,
    rollPeriod: 8,
    rollVariability: 0.3,
    pitch: 0,
    pitchAmplitude: 2.5,
    pitchPeriod: 6.5,
    pitchVariability: 0.25,
    pitchEnabled: true,
    pitchStabilizationEnabled: true,
    pitchStabilizationStrength: 90,
    yaw: 8,
    yawAmplitude: 3.5,
    yawPeriod: 10,
    yawVariability: 0.3,
    yawEnabled: true,
    yawStabilizationEnabled: true,
    yawStabilizationStrength: 90,
    course: 8,
    steerage: 0,
    missionPlanEnabled: false,
    missionPlanHeading: 0,
    missionPlanOverlap: 20,
    missionOptimizeHeading: true,
    missionSkipTurns: false,
    missionWaypoints: [],
    seafloorPreset: "ridge",
  },
};
const attitudeCanvas = document.getElementById("attitude-graph-canvas");
const attitudeCtx = attitudeCanvas?.getContext("2d");
const nadirCanvas = document.getElementById("nadir-graph-canvas");
const nadirCtx = nadirCanvas?.getContext("2d");
const rawStripCanvas = document.getElementById("raw-strip-graph-canvas");
const rawStripCtx = rawStripCanvas?.getContext("2d");
const densityQualityCanvas = document.getElementById("density-quality-graph-canvas");
const densityQualityCtx = densityQualityCanvas?.getContext("2d");
const densityHistogramCanvas = document.getElementById("density-histogram-canvas");
const densityHistogramCtx = densityHistogramCanvas?.getContext("2d");
const densityCellsEl = document.getElementById("density-cells");
const densityCoverageEl = document.getElementById("density-coverage");
const densityComplianceLabelEl = document.getElementById("density-compliance-label");
const densityComplianceEl = document.getElementById("density-compliance");
const densitySecondaryLabelEl = document.getElementById("density-secondary-label");
const densitySecondaryEl = document.getElementById("density-secondary");
const densityPeakEl = document.getElementById("density-peak");
const densityTouchedAvgEl = document.getElementById("density-touched-avg");
const densityTouchedMedianEl = document.getElementById("density-touched-median");
const densityTouchedP95El = document.getElementById("density-touched-p95");
const densityQualityLegendLabelEl = document.getElementById("density-quality-legend-label");
const attitudeSamples = [];
const nadirDepthSamples = [];
const densityQualitySamples = [];
let rawStripSnapshot = null;
let rawStripLastPingIndex = -1;
const maxAttitudeSamples = 220;
const densityEnabled = Boolean(densityCellsEl && densityCoverageEl && densityComplianceEl && densityPeakEl);
let densitySettingsKey = "";
let densitySampleDomainKey = "";
const densityState = {
  gridKey: "",
  cellSize: Number(stateA.meshGridSpacing) || 4,
  minX: -1400,
  maxX: 1400,
  minZ: -1400,
  maxZ: 1400,
  cols: 700,
  rows: 700,
  totalCells: 700 * 700,
  counts: new Map(),
  stats: new Map(),
  countHistogram: new Map(),
  totalSoundings: 0,
  cellsHit: 0,
  maxCount: 0,
  version: 0,
  payload: null,
  lastPingIndex: -1,
  lastReport: null,
};
const adaptivePerf = {
  beamCap: 2048,
  backlogLevel: 0,
};

simWorker.onmessage = (event) => {
  const msg = event.data;
  if (msg.type === "snapshot") {
    pendingSnapshots.push(msg.snapshot);
    if (!msg.snapshot?.resetSnapshot) {
      appendAttitudeSample(msg.snapshot);
    }
  }
};

densityWorker.onmessage = (event) => {
  const msg = event.data;
  if (msg.type !== "densityReport") {
    return;
  }
  const report = msg.report || {};
  densityState.version = Number(report.version) || densityState.version;
  densityState.totalCells = Math.max(0, Number(report.totalCells) || 0);
  densityState.cellsHit = Math.max(0, Number(report.cellsHit) || 0);
  densityState.totalSoundings = Math.max(0, Number(report.totalSoundings) || 0);
  densityState.maxCount = Math.max(0, Number(report.maxCount) || 0);
  const entries = Array.isArray(report.countHistogramEntries) ? report.countHistogramEntries : [];
  densityState.countHistogram = new Map(entries);
  const statsEntries = Array.isArray(report.statsEntries) ? report.statsEntries : [];
  const statsMap = new Map();
  for (let i = 0; i < statsEntries.length; i += 1) {
    const e = statsEntries[i];
    if (!Array.isArray(e) || e.length < 7) continue;
    const idx = Number(e[0]);
    if (!Number.isFinite(idx)) continue;
    statsMap.set(idx, {
      count: Math.max(0, Number(e[1]) || 0),
      min: Number(e[2]) || 0,
      max: Number(e[3]) || 0,
      mean: Number(e[4]) || 0,
      stddev: Math.max(0, Number(e[5]) || 0),
      uncertainty: Math.max(0, Number(e[6]) || 0),
    });
  }
  densityState.stats = statsMap;
  densityState.lastReport = report;
  refreshDensityPayload();
  updateDensityStatsView();
  appendDensityQualitySample(report);
};

const ui = createUi({
  stateA,
  onStateChange: scheduleApplyStates,
  onPlaybackAction: handlePlaybackAction,
  onApplyPreset: handleApplyPreset,
  onSaveState: handleSaveState,
  onLoadState: handleLoadState,
  onLoadEtopoAoi: handleLoadEtopoAoi,
  onLoadTerrainFile: handleLoadTerrainFile,
  onBuildEtopoSurface: handleBuildEtopoSurface,
  onClearEtopoSurface: handleClearEtopoSurface,
  onLoadWreckStl: handleLoadWreckStl,
  onClearWreckStl: handleClearWreckStl,
  onGenerateMissionPlan: handleGenerateMissionPlan,
  onOptimizeMissionHeading: handleOptimizeMissionHeading,
  onPresetLockChange: (locked) => {
    presetLocked = locked;
    ui.setLocked(locked);
  },
});

function applyStates() {
  if (densityEnabled) {
    ensureDensityGrid();
    syncDensitySettings();
    refreshDensityPayload();
    updateDensityStatsView();
  }
  const densityPayload = densityEnabled && (stateA.meshGridEnabled || stateA.gridNodesEnabled) ? densityState.payload : null;
  viewA.setState(stateA, latestSnapshot, densityPayload, getMissionPayload());
}

let applyQueued = false;
function scheduleApplyStates() {
  syncWreckWorkerMesh();
  syncWorkerLoopHz();
  const skipTurns = Boolean(stateA.missionSkipTurns);
  if (skipTurns !== lastMissionSkipTurns) {
    lastMissionSkipTurns = skipTurns;
    if (missionPlanState.segments.length) {
      const rebuilt = buildMissionWaypointsFromSegments(missionPlanState.segments, skipTurns, missionPlanState.spacing);
      syncMissionPlanToState(missionPlanState.segments, rebuilt, missionPlanState.spacing, skipTurns);
      if (stateA.missionPlanEnabled && stateA.missionWaypoints.length > 1) {
        simWorker.postMessage({ type: "setState", state: stateA });
        simWorker.postMessage({ type: "reset" });
      }
    }
  }
  const missionPlanEnabled = Boolean(stateA.missionPlanEnabled);
  if (missionPlanEnabled !== lastMissionPlanEnabled) {
    lastMissionPlanEnabled = missionPlanEnabled;
    if (missionPlanEnabled && Array.isArray(stateA.missionWaypoints) && stateA.missionWaypoints.length > 1) {
      simWorker.postMessage({ type: "setState", state: stateA });
      simWorker.postMessage({ type: "reset" });
    }
  }
  simWorker.postMessage({ type: "setState", state: stateA });
  if (!playbackRunning && !sonarRecording) {
    simWorker.postMessage({ type: "preview", includeHits: false });
  }
  if (applyQueued) {
    return;
  }
  applyQueued = true;
  requestAnimationFrame(() => {
    applyQueued = false;
    applyStates();
  });
}

function syncWreckWorkerMesh() {
  const version = Number(viewA.getWreckMeshVersion?.() || 0);
  const triBudget = Math.max(20000, Math.min(1000000, Math.floor(Number(stateA.wreckSonarTriangles) || 300000)));
  const key = `${version}|${triBudget}`;
  if (key === wreckWorkerMeshKey) {
    return;
  }
  wreckWorkerMeshKey = key;
  if (version <= 0) {
    simWorker.postMessage({ type: "setWreckMesh", data: null });
    return;
  }
  const payload = viewA.buildWreckWorkerPayload?.(triBudget);
  if (!payload?.vertices?.buffer) {
    simWorker.postMessage({ type: "setWreckMesh", data: null });
    return;
  }
  simWorker.postMessage({ type: "setWreckMesh", data: payload }, [payload.vertices.buffer]);
}

function onResize() {
  viewA.resize();
}

window.addEventListener("resize", onResize);
onResize();
syncWorkerLoopHz();
simWorker.postMessage({ type: "setState", state: stateA });
simWorker.postMessage({ type: "setPerfBudget", data: { beamCap: adaptivePerf.beamCap } });
simWorker.postMessage({ type: "setTerrainData", data: null });
simWorker.postMessage({ type: "reset" });
simWorker.postMessage({ type: "pause" });
simWorker.postMessage({ type: "stopRecording" });
playbackRunning = false;
sonarRecording = false;
applyStates();
ui.setLocked(false);
ui.setRecordingMode(false);
ui.setEtopoStatus("No AOI loaded.");
ui.setEtopoProgress(0, false);
ui.setWreckStatus("No STL loaded.");
ui.setMissionPlanStatus("No mission plan generated.");
requestAnimationFrame(() => {
  viewA.snapCameraPreset("obliqueBehind");
  stateA.cameraMode = "follow";
  ui.setTrackingMode(stateA.cameraMode);
  scheduleApplyStates();
});

window.addEventListener("beforeunload", () => {
  simWorker.postMessage({ type: "stop" });
  densityWorker.terminate();
});

function animate(time) {
  updateFrameTiming(time);
  flushSnapshotQueue();
  updateAdaptivePerformanceBudget();
  drawAttitudeGraph();
  drawNadirGraph();
  drawRawStripGraph();
  drawDensityQualityGraph();
  drawDensityHistogramGraph();
  viewA.render(time);
  requestAnimationFrame(animate);
}

requestAnimationFrame(animate);

function handlePlaybackAction(action) {
  if (action === "play") {
    playbackRunning = true;
    simWorker.postMessage({ type: "resume" });
  } else if (action === "record") {
    sonarRecording = !sonarRecording;
    if (sonarRecording) {
      simWorker.postMessage({ type: "startRecording" });
    } else {
      simWorker.postMessage({ type: "stopRecording" });
    }
    ui.setRecordingMode(sonarRecording);
  } else if (action === "pause") {
    playbackRunning = false;
    simWorker.postMessage({ type: "pause" });
    if (!sonarRecording) {
      simWorker.postMessage({ type: "preview", includeHits: false });
    }
  } else if (action === "step") {
    simWorker.postMessage({ type: "step" });
  } else if (action === "toggleTracking") {
    stateA.cameraMode = stateA.cameraMode === "follow" ? "free" : "follow";
    ui.setTrackingMode(stateA.cameraMode);
    scheduleApplyStates();
  } else if (action === "reset") {
    playbackRunning = false;
    sonarRecording = false;
    ui.setRecordingMode(false);
    simWorker.postMessage({ type: "pause" });
    simWorker.postMessage({ type: "stopRecording" });
    simWorker.postMessage({ type: "reset" });
  } else if (action === "recenter") {
    viewA.recenterCamera();
  } else if (action === "reframeAoi") {
    viewA.reframeAoi();
  } else if (typeof action === "string" && action.startsWith("cameraPreset:")) {
    const presetId = action.slice("cameraPreset:".length);
    viewA.snapCameraPreset(presetId);
    stateA.cameraMode = "follow";
    ui.setTrackingMode(stateA.cameraMode);
    scheduleApplyStates();
  }
}

function handleApplyPreset(id) {
  const preset = presets[id];
  if (!preset) {
    return;
  }
  Object.assign(stateA, preset);
  simWorker.postMessage({ type: "setState", state: stateA });
  simWorker.postMessage({ type: "reset" });
  ui.syncControlOutputs();
  syncMissionPlanToState([], []);
  ui.setMissionPlanStatus("No mission plan generated.");
  scheduleApplyStates();
}

async function handleLoadEtopoAoi(file) {
  try {
    const text = await file.text();
    loadedAoiRing = parseAoiRingFromGeoJson(text);
    ui.setEtopoStatus(`AOI loaded from ${file.name}.`);
  } catch (error) {
    loadedAoiRing = null;
    ui.setEtopoStatus(`AOI load failed: ${error.message}`);
  }
}

async function handleLoadTerrainFile(file) {
  try {
    const text = await file.text();
    const terrain = parseImportedTerrainFile(text);
    setTerrainData(terrain);
    simWorker.postMessage({ type: "setTerrainData", data: terrain });
    stateA.seafloorPreset = "etopo_aoi";
    stateA.terrainRevision += 1;
    resetDensityCounts(true);
    syncMissionPlanToState([], []);
    ui.syncControlOutputs();
    simWorker.postMessage({ type: "setState", state: stateA });
    simWorker.postMessage({ type: "reset" });
    scheduleApplyStates();
    const ext = getTerrainExtents(terrain);
    const type = getTerrainType(terrain);
    const xSpan = ext ? (ext.maxX - ext.minX) : 0;
    const zSpan = ext ? (ext.maxZ - ext.minZ) : 0;
    const detail = type === "tin"
      ? `${Math.floor((terrain.vertices?.length || 0) / 3).toLocaleString()} verts / ${Math.floor((terrain.triangles?.length || 0) / 3).toLocaleString()} tris`
      : `${Number(terrain.width || 0)}x${Number(terrain.height || 0)}`;
    ui.setEtopoStatus(`Terrain loaded from ${file.name} (${type || "unknown"} · ${detail} · ${xSpan.toFixed(1)}m x ${zSpan.toFixed(1)}m).`);
    ui.setEtopoProgress(0, false);
  } catch (error) {
    console.error("Terrain load failed:", error);
    ui.setEtopoStatus(`Terrain load failed: ${error.message}`);
    ui.setEtopoProgress(0, false);
  }
}

function parseImportedTerrainFile(text) {
  const parsed = JSON.parse(String(text || ""));
  const payload = parsed?.terrain && typeof parsed.terrain === "object" ? parsed.terrain : parsed;
  const type = getTerrainType(payload);
  if (type === "grid") {
    return normalizeGridTerrain(payload);
  }
  if (type === "tin") {
    return normalizeTinTerrain(payload);
  }
  throw new Error("Unsupported terrain file. Expected grid or tin terrain JSON.");
}

function normalizeGridTerrain(payload) {
  const width = Math.max(2, Math.floor(Number(payload?.width) || 0));
  const height = Math.max(2, Math.floor(Number(payload?.height) || 0));
  const values = toFloat32Array(payload?.values);
  if (!values || values.length < width * height) {
    throw new Error("Grid terrain is missing valid values.");
  }
  const ext = getTerrainExtents(payload);
  if (!ext) {
    throw new Error("Grid terrain is missing extents.");
  }
  return {
    type: "grid",
    width,
    height,
    minX: ext.minX,
    maxX: ext.maxX,
    minZ: ext.minZ,
    maxZ: ext.maxZ,
    values,
    minDepth: Number(payload?.minDepth),
    maxDepth: Number(payload?.maxDepth),
  };
}

function normalizeTinTerrain(payload) {
  const vertices = toFloat32Array(payload?.vertices);
  const triangles = toUint32Array(payload?.triangles);
  if (!vertices || vertices.length < 9 || !triangles || triangles.length < 3) {
    throw new Error("TIN terrain is missing valid vertices/triangles.");
  }
  const ext = getTerrainExtents(payload) || computeTinExtents(vertices);
  const terrain = {
    type: "tin",
    minX: ext.minX,
    maxX: ext.maxX,
    minZ: ext.minZ,
    maxZ: ext.maxZ,
    minY: Number.isFinite(Number(payload?.minY)) ? Number(payload.minY) : Number(ext.minY),
    maxY: Number.isFinite(Number(payload?.maxY)) ? Number(payload.maxY) : Number(ext.maxY),
    vertices,
    triangles,
  };
  const existingOffsets = toUint32Array(payload?.binOffsets);
  const existingTriangleIndices = toUint32Array(payload?.binTriangleIndices);
  if (existingOffsets && existingTriangleIndices && Number(payload?.binCols) > 0 && Number(payload?.binRows) > 0) {
    return {
      ...terrain,
      binCols: Math.max(1, Math.floor(Number(payload.binCols))),
      binRows: Math.max(1, Math.floor(Number(payload.binRows))),
      binOffsets: existingOffsets,
      binTriangleIndices: existingTriangleIndices,
    };
  }
  return {
    ...terrain,
    ...buildTinSpatialBins(terrain),
  };
}

function toFloat32Array(value) {
  if (value instanceof Float32Array) return value;
  if (ArrayBuffer.isView(value) || Array.isArray(value)) return Float32Array.from(value);
  return null;
}

function toUint32Array(value) {
  if (value instanceof Uint32Array) return value;
  if (ArrayBuffer.isView(value) || Array.isArray(value)) return Uint32Array.from(value);
  return null;
}

function computeTinExtents(vertices) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < vertices.length; i += 3) {
    const x = Number(vertices[i]);
    const y = Number(vertices[i + 1]);
    const z = Number(vertices[i + 2]);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  if (![minX, maxX, minY, maxY, minZ, maxZ].every(Number.isFinite)) {
    throw new Error("Unable to compute TIN terrain extents.");
  }
  return { minX, maxX, minY, maxY, minZ, maxZ };
}

async function handleBuildEtopoSurface() {
  if (!loadedAoiRing) {
    ui.setEtopoStatus("Load an AOI GeoJSON polygon first.");
    ui.setEtopoProgress(0, false);
    return;
  }
  try {
    ui.setEtopoStatus("Sampling ETOPO surface...");
    ui.setEtopoProgress(0, true);
    const terrain = await buildEtopoSurfaceFromAoi(loadedAoiRing, stateA.etopoGridSize, (done, total) => {
      ui.setEtopoProgress(done / Math.max(1, total), true);
    });
    ui.setEtopoProgress(1, true);
    setTerrainData(terrain);
    simWorker.postMessage({ type: "setTerrainData", data: terrain });
    stateA.seafloorPreset = "etopo_aoi";
    stateA.terrainRevision += 1;
    resetDensityCounts(true);
    syncMissionPlanToState([], []);
    ui.syncControlOutputs();
    simWorker.postMessage({ type: "setState", state: stateA });
    simWorker.postMessage({ type: "reset" });
    scheduleApplyStates();
    const misses = Number(terrain.failedSamples || 0);
    const filled = Number(terrain.filledSamples || 0);
    const dMin = Number(terrain.minDepth);
    const dMax = Number(terrain.maxDepth);
    const relief = Number.isFinite(dMin) && Number.isFinite(dMax) ? (dMax - dMin).toFixed(1) : "?";
    const minTxt = Number.isFinite(dMin) ? dMin.toFixed(1) : "?";
    const maxTxt = Number.isFinite(dMax) ? dMax.toFixed(1) : "?";
    if (misses > 0 || filled > 0) {
      ui.setEtopoStatus(
        `ETOPO ready (${terrain.width}x${terrain.height}), min ${minTxt} m, max ${maxTxt} m, relief ${relief} m, misses ${misses}, filled ${filled}.`,
      );
    } else {
      ui.setEtopoStatus(`ETOPO ready (${terrain.width}x${terrain.height}), min ${minTxt} m, max ${maxTxt} m, relief ${relief} m.`);
    }
  } catch (error) {
    console.error("ETOPO surface build failed:", error);
    ui.setEtopoStatus(`ETOPO build failed: ${error.message}`);
    ui.setEtopoProgress(0, false);
  }
}

function handleClearEtopoSurface() {
  clearTerrainData();
  simWorker.postMessage({ type: "setTerrainData", data: null });
  stateA.terrainRevision += 1;
  if (stateA.seafloorPreset === "etopo_aoi") {
    stateA.seafloorPreset = "flat";
  }
  resetDensityCounts(true);
  syncMissionPlanToState([], []);
  ui.syncControlOutputs();
  simWorker.postMessage({ type: "setState", state: stateA });
  simWorker.postMessage({ type: "reset" });
  scheduleApplyStates();
  ui.setEtopoStatus("ETOPO surface cleared.");
  ui.setEtopoProgress(0, false);
}

async function handleLoadWreckStl(file) {
  try {
    ui.setWreckStatus(`Loading ${file.name}...`);
    const stats = await viewA.loadWreckStlFile(file);
    const recommended = recommendWreckImportSettings(stats?.sourceTriangles);
    wreckWorkerMeshKey = "";
    stateA.wreckEnabled = true;
    stateA.wreckVisible = true;
    stateA.wreckRoll = 0;
    stateA.wreckPitch = 0;
    stateA.wreckOffsetZ = 0;
    stateA.wreckSonarTriangles = recommended.wreckSonarTriangles;
    stateA.beamCount = recommended.beamCount;
    stateA.accuracyMode = recommended.accuracyMode;
    ui.syncControlOutputs();
    simWorker.postMessage({ type: "setState", state: stateA });
    simWorker.postMessage({ type: "reset" });
    scheduleApplyStates();
    const dims = stats
      ? `${stats.length.toFixed(1)} x ${stats.beam.toFixed(1)} x ${stats.height.toFixed(1)} m`
      : "unknown dimensions";
    const faces = Math.max(0, Math.floor(Number(stats?.sourceTriangles) || 0));
    ui.setWreckStatus(
      `Loaded ${file.name} (${dims}), faces ${faces.toLocaleString()}, auto: ${recommended.label} (${recommended.accuracyMode}, beams ${recommended.beamCount}, sonar tris ${recommended.wreckSonarTriangles.toLocaleString()}).`,
    );
  } catch (error) {
    console.error("Wreck STL load failed:", error);
    ui.setWreckStatus(`STL load failed: ${error.message}`);
  }
}

function handleClearWreckStl() {
  viewA.clearWreckStl();
  wreckWorkerMeshKey = "";
  stateA.wreckEnabled = false;
  ui.syncControlOutputs();
  simWorker.postMessage({ type: "setState", state: stateA });
  simWorker.postMessage({ type: "reset" });
  scheduleApplyStates();
  ui.setWreckStatus("No STL loaded.");
}

function recommendWreckImportSettings(sourceTriangles) {
  const faces = Math.max(0, Math.floor(Number(sourceTriangles) || 0));
  if (faces <= 80_000) {
    return { label: "fast", wreckSonarTriangles: 160_000, beamCount: 512, accuracyMode: "fast" };
  }
  if (faces <= 250_000) {
    return { label: "balanced", wreckSonarTriangles: 320_000, beamCount: 512, accuracyMode: "fast" };
  }
  if (faces <= 900_000) {
    return { label: "high fidelity", wreckSonarTriangles: 520_000, beamCount: 1024, accuracyMode: "full" };
  }
  return { label: "extreme", wreckSonarTriangles: 760_000, beamCount: 1024, accuracyMode: "full" };
}

function handleSaveState() {
  const payload = {
    version: 1,
    savedAt: new Date().toISOString(),
    state: { ...stateA },
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp = payload.savedAt.replace(/[:.]/g, "-");
  a.href = url;
  a.download = `multibeam-state-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function handleLoadState(file) {
  try {
    const text = await file.text();
    const parsed = JSON.parse(text);
    const loaded = parsed?.state ?? parsed;
    if (!loaded || typeof loaded !== "object") {
      throw new Error("No state object found.");
    }
    const allowedKeys = Object.keys(baseScenarioState);
    for (const key of allowedKeys) {
      if (Object.prototype.hasOwnProperty.call(loaded, key)) {
        stateA[key] = loaded[key];
      }
    }
    ui.syncControlOutputs();
    resetDensityCounts(true);
    simWorker.postMessage({ type: "setState", state: stateA });
    simWorker.postMessage({ type: "reset" });
    scheduleApplyStates();
  } catch (error) {
    console.error("Failed to load state file:", error);
  }
}

function flushSnapshotQueue() {
  if (!pendingSnapshots.length) {
    return;
  }
  const maxQueue = 1200;
  const retainNewest = 320;
  if (pendingSnapshots.length > maxQueue) {
    pendingSnapshots.splice(0, pendingSnapshots.length - retainNewest);
  }
  const processedSnapshots = [];
  const maxPerFrame = 120;
  while (pendingSnapshots.length && processedSnapshots.length < maxPerFrame) {
    const snapshot = pendingSnapshots.shift();
    latestSnapshot = snapshot;
    if (densityEnabled && !snapshot?.resetSnapshot) {
      processDensityFromSnapshot(snapshot);
    }
    processedSnapshots.push(snapshot);
  }
  const densityPayload = densityEnabled && (stateA.meshGridEnabled || stateA.gridNodesEnabled) ? densityState.payload : null;
  viewA.setState(stateA, latestSnapshot, densityPayload, getMissionPayload(), processedSnapshots);
}

function handleGenerateMissionPlan() {
  const ext = getCurrentSeafloorExtents();
  const overlap = Math.max(0, Math.min(80, Number(stateA.missionPlanOverlap) || 0));
  let headingDeg = Number(stateA.missionPlanHeading) || 0;
  if (stateA.missionOptimizeHeading) {
    const optimized = optimizeMissionHeading(ext, overlap, headingDeg);
    headingDeg = optimized.heading;
    stateA.missionPlanHeading = headingDeg;
    ui.syncControlOutputs();
    ui.setMissionPlanStatus(`Heading optimized to ${headingDeg.toFixed(1)} deg (predicted gap ${(optimized.uncoveredPct).toFixed(1)}%).`);
  }
  const spacing = estimateRepresentativeLineSpacing(ext, overlap);
  const generated = buildLinePlanFromExtents(ext, headingDeg, spacing, overlap, { includeInfill: true });
  const waypoints = buildMissionWaypointsFromSegments(generated.segments, Boolean(stateA.missionSkipTurns), spacing);
  if (!generated.segments.length || waypoints.length < 2) {
    syncMissionPlanToState([], []);
    ui.setMissionPlanStatus("Mission plan generation failed: no valid lines for current extents/settings.");
    scheduleApplyStates();
    return;
  }
  syncMissionPlanToState(generated.segments, waypoints, spacing, Boolean(stateA.missionSkipTurns));
  simWorker.postMessage({ type: "setState", state: stateA });
  if (stateA.missionPlanEnabled) {
    simWorker.postMessage({ type: "reset" });
  }
  scheduleApplyStates();
  ui.setMissionPlanStatus(
    `Plan ready: ${generated.segments.length} lines (${generated.infillCount} infill), spacing ${generated.minSpacing.toFixed(1)}-${generated.maxSpacing.toFixed(1)} m, predicted uncovered ${generated.estimatedUncoveredPct.toFixed(2)}%, route waypoints ${waypoints.length}${stateA.missionSkipTurns ? " (skip turns)" : ""}.`,
  );
}

function handleOptimizeMissionHeading() {
  const ext = getCurrentSeafloorExtents();
  const overlap = Math.max(0, Math.min(80, Number(stateA.missionPlanOverlap) || 0));
  const seed = Number(stateA.missionPlanHeading) || 0;
  const optimized = optimizeMissionHeading(ext, overlap, seed);
  stateA.missionPlanHeading = optimized.heading;
  ui.syncControlOutputs();
  scheduleApplyStates();
  ui.setMissionPlanStatus(
    `Best heading ${optimized.heading.toFixed(1)} deg (predicted uncovered ${optimized.uncoveredPct.toFixed(1)}%, lines ${optimized.lines}).`,
  );
}

function syncMissionPlanToState(segments, waypoints, spacing = 0, skipTurns = false) {
  missionPlanState.segments = segments || [];
  missionPlanState.waypoints = waypoints || [];
  missionPlanState.spacing = Number(spacing) || 0;
  missionPlanState.skipTurns = Boolean(skipTurns);
  missionPlanState.version += 1;
  stateA.missionWaypoints = missionPlanState.waypoints.map((p) => ({
    x: Number(p.x) || 0,
    z: Number(p.z) || 0,
    mode: p.mode || "line",
  }));
}

function getMissionPayload() {
  return {
    version: missionPlanState.version,
    segments: missionPlanState.segments,
    spacing: missionPlanState.spacing,
  };
}

function syncWorkerLoopHz() {
  const targetHz = getTargetWorkerHz(stateA.pingRate);
  if (targetHz === workerLoopHz) {
    return;
  }
  workerLoopHz = targetHz;
  simWorker.postMessage({ type: "start", hz: workerLoopHz });
}

function getTargetWorkerHz(pingRate) {
  const rate = Math.max(1, Number(pingRate) || 1);
  return Math.min(240, Math.max(60, Math.ceil(rate * 8)));
}

function updateFrameTiming(time) {
  const t = Number(time) || 0;
  if (lastFrameTime > 0) {
    const dt = t - lastFrameTime;
    if (dt > 0 && dt < 1000) {
      frameTimes.push(dt);
      if (frameTimes.length > 30) {
        frameTimes.shift();
      }
    }
  }
  lastFrameTime = t;
}

function updateAdaptivePerformanceBudget() {
  const backlog = pendingSnapshots.length;
  const avgFrameMs = frameTimes.length
    ? frameTimes.reduce((acc, v) => acc + v, 0) / frameTimes.length
    : 16.67;
  const desiredBacklogLevel = backlog > 140 ? 3 : backlog > 70 ? 2 : backlog > 30 ? 1 : 0;
  const desiredFrameLevel = avgFrameMs > 30 ? 3 : avgFrameMs > 23 ? 2 : avgFrameMs > 18 ? 1 : 0;
  const level = Math.max(desiredBacklogLevel, desiredFrameLevel);
  if (level === adaptivePerf.backlogLevel) {
    return;
  }
  adaptivePerf.backlogLevel = level;
  let beamCap = 2048;
  if (level === 1) {
    beamCap = 1536;
  } else if (level === 2) {
    beamCap = 1024;
  } else if (level >= 3) {
    beamCap = 640;
  }
  adaptivePerf.beamCap = beamCap;
  const key = `${beamCap}`;
  if (key === perfBudgetKey) {
    return;
  }
  perfBudgetKey = key;
  simWorker.postMessage({
    type: "setPerfBudget",
    data: { beamCap },
  });
}

function appendAttitudeSample(snapshot) {
  const attitude = snapshot?.attitude;
  if (!attitude) {
    return;
  }
  attitudeSamples.push({
    roll: attitude.roll ?? 0,
    pitch: attitude.pitch ?? 0,
    yaw: attitude.yaw ?? 0,
  });
  if (attitudeSamples.length > maxAttitudeSamples) {
    attitudeSamples.splice(0, attitudeSamples.length - maxAttitudeSamples);
  }

  const hits = snapshot?.hits;
  if (Array.isArray(hits) && hits.length) {
    let best = hits[0];
    let bestAbsU = Math.abs(Number(hits[0]?.u) || 0);
    for (let i = 1; i < hits.length; i += 1) {
      const absU = Math.abs(Number(hits[i]?.u) || 0);
      if (absU < bestAbsU) {
        bestAbsU = absU;
        best = hits[i];
      }
    }
    const nadirDepth = Math.max(0, -(Number(best?.point?.[1]) || 0));
    nadirDepthSamples.push(nadirDepth);
    if (nadirDepthSamples.length > maxAttitudeSamples) {
      nadirDepthSamples.splice(0, nadirDepthSamples.length - maxAttitudeSamples);
    }
  }
  appendRawStripSample(snapshot);
}

function appendRawStripSample(snapshot) {
  if (!snapshot || !Array.isArray(snapshot.hits) || snapshot.hits.length < 2) {
    return;
  }
  const pingIndex = Number(snapshot.pingIndex);
  if (!Number.isFinite(pingIndex)) {
    rawStripSnapshot = snapshot;
    return;
  }
  if (pingIndex === 0 || pingIndex < rawStripLastPingIndex) {
    rawStripLastPingIndex = pingIndex;
    rawStripSnapshot = snapshot;
    return;
  }

  const pingRate = Math.max(1, Number(stateA.pingRate) || 1);
  const hz = Math.max(1, Number(stateA.rawStripUpdateRate) || 1);
  const pingStride = Math.max(1, Math.round(pingRate / hz));
  if (pingIndex === rawStripLastPingIndex || (pingIndex % pingStride) !== 0) {
    return;
  }
  rawStripLastPingIndex = pingIndex;
  rawStripSnapshot = snapshot;
}

function ensureDensityGrid() {
  const ext = getCurrentSeafloorExtents();
  const cell = Math.max(1, Number(stateA.meshGridSpacing) || 4);
  const spanX = Math.max(1, ext.maxX - ext.minX);
  const spanZ = Math.max(1, ext.maxZ - ext.minZ);
  const cols = Math.max(1, Math.ceil(spanX / cell));
  const rows = Math.max(1, Math.ceil(spanZ / cell));
  const gridKey = [
    stateA.seafloorPreset,
    stateA.terrainRevision,
    cell,
    ext.minX.toFixed(3),
    ext.maxX.toFixed(3),
    ext.minZ.toFixed(3),
    ext.maxZ.toFixed(3),
  ].join("|");
  const sampleDomainKey = [
    stateA.seafloorPreset,
    stateA.terrainRevision,
    ext.minX.toFixed(3),
    ext.maxX.toFixed(3),
    ext.minZ.toFixed(3),
    ext.maxZ.toFixed(3),
  ].join("|");
  if (gridKey === densityState.gridKey) {
    return;
  }

  const domainChanged = sampleDomainKey !== densitySampleDomainKey;
  densitySampleDomainKey = sampleDomainKey;
  if (domainChanged) {
    densityWorker.postMessage({ type: "reset", clearSamples: true });
  }

  densityState.gridKey = gridKey;
  densityState.cellSize = cell;
  densityState.minX = ext.minX;
  densityState.maxX = ext.maxX;
  densityState.minZ = ext.minZ;
  densityState.maxZ = ext.maxZ;
  densityState.cols = cols;
  densityState.rows = rows;
  densityState.totalCells = cols * rows;
  densityWorker.postMessage({
    type: "configureGrid",
    grid: {
      gridKey,
      cellSize: densityState.cellSize,
      minX: densityState.minX,
      maxX: densityState.maxX,
      minZ: densityState.minZ,
      maxZ: densityState.maxZ,
      cols: densityState.cols,
      rows: densityState.rows,
    },
  });
}

function resetDensityCounts(clearGrid = false) {
  if (clearGrid) {
    densityState.gridKey = "";
    densitySampleDomainKey = "";
  }
  densityState.counts = new Map();
  densityState.stats = new Map();
  densityState.countHistogram = new Map();
  densityState.totalSoundings = 0;
  densityState.cellsHit = 0;
  densityState.maxCount = 0;
  densityState.lastPingIndex = -1;
  densityState.lastReport = null;
  densityQualitySamples.length = 0;
  densityQualitySamples.push(0);
  densityState.version += 1;
  densityWorker.postMessage({ type: "reset", clearSamples: Boolean(clearGrid) });
  refreshDensityPayload();
  updateDensityStatsView();
}

function processDensityFromSnapshot(snapshot) {
  if (!densityEnabled) {
    return;
  }
  if (stateA.densityPaintingEnabled === false) {
    return;
  }
  if (!snapshot || !Array.isArray(snapshot.hits) || !snapshot.hits.length) {
    return;
  }
  if (!stateA.meshGridEnabled && !stateA.gridNodesEnabled) {
    return;
  }
  ensureDensityGrid();
  const pingIndex = Number(snapshot.pingIndex) || 0;
  const backlogStride = pendingSnapshots.length > 100 ? 4 : (pendingSnapshots.length > 40 ? 2 : 1);
  if ((pingIndex % backlogStride) !== 0) {
    return;
  }
  densityWorker.postMessage({
    type: "processSnapshot",
    snapshot: {
      pingIndex,
      hits: snapshot.hits,
    },
  });
}

function refreshDensityPayload() {
  densityState.payload = {
    version: densityState.version,
    minX: densityState.minX,
    maxX: densityState.maxX,
    minZ: densityState.minZ,
    maxZ: densityState.maxZ,
    cols: densityState.cols,
    rows: densityState.rows,
    cellSize: densityState.cellSize,
    counts: densityState.counts,
    stats: densityState.stats,
    maxCount: densityState.maxCount,
    targetPerCell: getDensityPrimaryTarget(),
    gridNodesEnabled: Boolean(stateA.gridNodesEnabled),
    gridSurfaceMode: String(stateA.gridSurfaceMode || "triangulated"),
    gridSurfaceHillshade: Boolean(stateA.gridSurfaceHillshade),
    gridSurfaceMaskNoData: Boolean(stateA.gridSurfaceMaskNoData),
    gridNodeMetric: String(stateA.gridNodeMetric || "mean"),
    gridNodeColorRamp: String(stateA.gridNodeColorRamp || "viridis"),
    gridNodeOpacity: Math.max(0, Math.min(1, Number(stateA.gridNodeOpacity) || 0.8)),
    gridNodeSize: Math.max(1, Math.min(24, Number(stateA.gridNodeSize) || 3)),
  };
}

function updateDensityStatsView() {
  if (!densityCellsEl || !densityCoverageEl || !densityComplianceEl || !densityPeakEl) {
    return;
  }
  const report = densityState.lastReport;
  const total = Math.max(0, densityState.totalCells);
  const hit = Math.max(0, densityState.cellsHit);
  const primaryTarget = report ? Math.max(1, Number(report.primaryTarget) || getDensityPrimaryTarget()) : getDensityPrimaryTarget();
  const secondaryTarget = report ? Math.max(1, Number(report.secondaryTarget) || getDensitySecondaryTarget()) : getDensitySecondaryTarget();
  const basis = report?.basis === "aoi" ? "aoi" : getDensityQualityBasis();
  const primaryCompliance = report && Number.isFinite(Number(report.primaryCompliance))
    ? Number(report.primaryCompliance)
    : (() => {
      const primaryMeeting = getDensityMeetingCells(primaryTarget);
      const basisDenominator = basis === "aoi" ? total : hit;
      return basisDenominator > 0 ? (primaryMeeting / basisDenominator) * 100 : 0;
    })();
  const secondaryCompliance = report && Number.isFinite(Number(report.secondaryCompliance))
    ? Number(report.secondaryCompliance)
    : (() => {
      const secondaryMeeting = getDensityMeetingCells(secondaryTarget);
      const basisDenominator = basis === "aoi" ? total : hit;
      return basisDenominator > 0 ? (secondaryMeeting / basisDenominator) * 100 : 0;
    })();
  const touchedAvg = hit > 0 ? densityState.totalSoundings / hit : 0;
  const touchedMedian = report && Number.isFinite(Number(report.touchedMedian))
    ? Number(report.touchedMedian)
    : getDensityCountQuantile(0.5);
  const touchedP95 = report && Number.isFinite(Number(report.touchedP95))
    ? Number(report.touchedP95)
    : getDensityCountQuantile(0.95);
  const coverage = total > 0 ? (hit / total) * 100 : 0;
  const basisLabel = basis === "aoi" ? "AOI" : "touched";
  densityCellsEl.textContent = `${hit.toLocaleString()} / ${total.toLocaleString()}`;
  densityCoverageEl.textContent = `${coverage.toFixed(1)}%`;
  densityComplianceEl.textContent = `${primaryCompliance.toFixed(1)}%`;
  if (densityComplianceLabelEl) {
    densityComplianceLabelEl.textContent = `>=${primaryTarget} / ${basisLabel}`;
  }
  if (densitySecondaryEl) {
    densitySecondaryEl.textContent = `${secondaryCompliance.toFixed(1)}%`;
  }
  if (densitySecondaryLabelEl) {
    densitySecondaryLabelEl.textContent = `>=${secondaryTarget} / ${basisLabel}`;
  }
  densityPeakEl.textContent = `${densityState.maxCount.toLocaleString()}`;
  if (densityTouchedAvgEl) {
    densityTouchedAvgEl.textContent = `${touchedAvg.toFixed(1)}`;
  }
  if (densityTouchedMedianEl) {
    densityTouchedMedianEl.textContent = `${Math.round(touchedMedian).toLocaleString()}`;
  }
  if (densityTouchedP95El) {
    densityTouchedP95El.textContent = `${Math.round(touchedP95).toLocaleString()}`;
  }
  if (densityQualityLegendLabelEl) {
    densityQualityLegendLabelEl.textContent = `>=${primaryTarget} / ${basisLabel} (%)`;
  }
}

function getDensityPrimaryTarget() {
  return Math.max(1, Math.round(Number(stateA.densityTargetPrimary) || 5));
}

function getDensitySecondaryTarget() {
  return Math.max(1, Math.round(Number(stateA.densityTargetSecondary) || 10));
}

function getDensityQualityBasis() {
  return stateA.densityQualityBasis === "aoi" ? "aoi" : "touched";
}

function getDensityMeetingCells(threshold) {
  const t = Math.max(1, Number(threshold) || 1);
  let meeting = 0;
  densityState.countHistogram.forEach((cellCount, hitsPerCell) => {
    if (hitsPerCell >= t) {
      meeting += cellCount;
    }
  });
  return meeting;
}

function syncDensitySettings() {
  const primaryTarget = getDensityPrimaryTarget();
  const secondaryTarget = getDensitySecondaryTarget();
  const basis = getDensityQualityBasis();
  const nextKey = `${primaryTarget}|${secondaryTarget}|${basis}`;
  if (nextKey === densitySettingsKey) {
    return;
  }
  densitySettingsKey = nextKey;
  densityWorker.postMessage({
    type: "settings",
    primaryTarget,
    secondaryTarget,
    basis,
  });
}

function updateDensityCountHistogram(prev, next) {
  if (prev > 0) {
    const prevCount = (densityState.countHistogram.get(prev) || 0) - 1;
    if (prevCount > 0) {
      densityState.countHistogram.set(prev, prevCount);
    } else {
      densityState.countHistogram.delete(prev);
    }
  }
  if (next > 0) {
    const nextCount = (densityState.countHistogram.get(next) || 0) + 1;
    densityState.countHistogram.set(next, nextCount);
  }
}

function getDensityCountQuantile(q) {
  const touched = Math.max(0, densityState.cellsHit);
  if (touched <= 0 || densityState.countHistogram.size === 0) {
    return 0;
  }
  const clampedQ = Math.max(0, Math.min(1, Number(q) || 0));
  const rank = Math.max(1, Math.ceil(clampedQ * touched));
  let acc = 0;
  const sortedCounts = Array.from(densityState.countHistogram.keys()).sort((a, b) => a - b);
  for (let i = 0; i < sortedCounts.length; i += 1) {
    const count = sortedCounts[i];
    acc += densityState.countHistogram.get(count) || 0;
    if (acc >= rank) {
      return count;
    }
  }
  return sortedCounts[sortedCounts.length - 1] || 0;
}

function getDensityCellIndex(x, z) {
  const col = Math.floor((x - densityState.minX) / densityState.cellSize);
  const row = Math.floor((z - densityState.minZ) / densityState.cellSize);
  if (col < 0 || row < 0 || col >= densityState.cols || row >= densityState.rows) {
    return -1;
  }
  return row * densityState.cols + col;
}

function getCurrentSeafloorExtents() {
  const terrain = stateA.seafloorPreset === "etopo_aoi" ? getTerrainData() : null;
  if (terrain) {
    return {
      minX: Number(terrain.minX) || -1400,
      maxX: Number(terrain.maxX) || 1400,
      minZ: Number(terrain.minZ) || -1400,
      maxZ: Number(terrain.maxZ) || 1400,
    };
  }
  if (stateA.seafloorPreset === "shipwreck_titanic") {
    return { minX: -420, maxX: 980, minZ: -460, maxZ: 460 };
  }
  return { minX: -1400, maxX: 1400, minZ: -1400, maxZ: 1400 };
}

function sampleSurveyDepthAt(x, z) {
  const terrain = stateA.seafloorPreset === "etopo_aoi" ? getTerrainData() : null;
  const y = terrain
    ? sampleSeafloorHeightWithDataStrict(stateA.seafloorPreset, stateA.range, x, z, terrain)
    : sampleSeafloorHeight(stateA.seafloorPreset, stateA.range, x, z);
  if (!Number.isFinite(y)) {
    return null;
  }
  const depth = Math.max(0, -y);
  const maxDepth = Math.max(1, Number(stateA.range) || 1);
  if (depth > maxDepth) {
    return null;
  }
  return depth;
}

function buildLinePlanFromExtents(ext, headingDeg, nominalSpacing, overlapPct, options = {}) {
  const minX = Number(ext.minX) || -1400;
  const maxX = Number(ext.maxX) || 1400;
  const minZ = Number(ext.minZ) || -1400;
  const maxZ = Number(ext.maxZ) || 1400;
  const corners = [
    [minX, minZ],
    [maxX, minZ],
    [maxX, maxZ],
    [minX, maxZ],
  ];
  const headingRad = (headingDeg * Math.PI) / 180;
  const dirX = Math.cos(headingRad);
  const dirZ = -Math.sin(headingRad);
  const nrmX = -dirZ;
  const nrmZ = dirX;
  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;
  for (let i = 0; i < corners.length; i += 1) {
    const x = corners[i][0];
    const z = corners[i][1];
    const u = x * dirX + z * dirZ;
    const v = x * nrmX + z * nrmZ;
    minU = Math.min(minU, u);
    maxU = Math.max(maxU, u);
    minV = Math.min(minV, v);
    maxV = Math.max(maxV, v);
  }
  const stepNominal = Math.max(2, Number(nominalSpacing) || 20);
  const lineDefs = [];
  const spacingSamples = [];
  let index = 0;
  const envelopePad = Math.max(2, stepNominal * 0.5);
  let v = minV - envelopePad;
  let prevStep = stepNominal;
  let safety = 0;
  while (v <= maxV + envelopePad + 1e-6 && safety < 2000) {
    lineDefs.push({ v, uMin: minU, uMax: maxU, kind: "main" });
    const nextAdaptiveStep = estimateAdaptiveSpacingAtV(
      minU,
      maxU,
      v,
      dirX,
      dirZ,
      nrmX,
      nrmZ,
      overlapPct,
      stepNominal,
    );
    const blendedStep = Math.max(2, Math.min(stepNominal, prevStep * 0.15 + nextAdaptiveStep * 0.85));
    spacingSamples.push(blendedStep);
    prevStep = blendedStep;
    v += blendedStep;
    index += 1;
    safety += 1;
  }
  const minSpacing = spacingSamples.length ? Math.min(...spacingSamples) : stepNominal;
  const maxSpacing = spacingSamples.length ? Math.max(...spacingSamples) : stepNominal;
  let infillCount = 0;
  let estimatedUncoveredPct = 0;
  if (options.includeInfill !== false) {
    const coverageSamples = buildCoverageSamples(ext, 3200, { dirX, dirZ, nrmX, nrmZ });
    const maxInfill = 14;
    const maxPasses = 3;
    let pass = 0;
    while (pass < maxPasses && infillCount < maxInfill) {
      const segmentsNow = materializeSegmentsFromLineDefs(lineDefs, dirX, dirZ, nrmX, nrmZ, stepNominal);
      const coverage = assessCoverageForSegments(coverageSamples, segmentsNow);
      estimatedUncoveredPct = coverage.uncoveredPct;
      if (coverage.uncoveredSamples.length < 8 || coverage.uncoveredPct < 0.15) {
        break;
      }
      const infillDefs = buildInfillLineDefsFromCoverage(
        coverage.uncoveredSamples,
        lineDefs,
        minU,
        maxU,
        minV,
        maxV,
        stepNominal,
        maxSpacing,
      );
      if (!infillDefs.length) {
        break;
      }
      const room = Math.max(0, maxInfill - infillCount);
      const toAdd = infillDefs.slice(0, room);
      if (!toAdd.length) {
        break;
      }
      infillCount += toAdd.length;
      lineDefs.push(...toAdd);
      lineDefs.sort((a, b) => a.v - b.v);
      pass += 1;
    }
    if (pass >= maxPasses || infillCount >= maxInfill) {
      const segmentsNow = materializeSegmentsFromLineDefs(lineDefs, dirX, dirZ, nrmX, nrmZ, stepNominal);
      estimatedUncoveredPct = assessCoverageForSegments(coverageSamples, segmentsNow).uncoveredPct;
    }
  }
  const segments = materializeSegmentsFromLineDefs(lineDefs, dirX, dirZ, nrmX, nrmZ, stepNominal);
  let totalLength = 0;
  for (let i = 0; i < segments.length; i += 1) {
    const s = segments[i];
    totalLength += Math.hypot(s.end[0] - s.start[0], s.end[1] - s.start[1]);
  }
  return { segments, minSpacing, maxSpacing, totalLength, infillCount, estimatedUncoveredPct };
}

function materializeSegmentsFromLineDefs(lineDefs, dirX, dirZ, nrmX, nrmZ, nominalSpacing = 20) {
  const ordered = [...lineDefs].sort((a, b) => a.v - b.v);
  const segments = [];
  for (let i = 0; i < ordered.length; i += 1) {
    const ln = ordered[i];
    const intervals = getValidSurveyIntervalsAlongLine(ln.uMin, ln.uMax, ln.v, dirX, dirZ, nrmX, nrmZ, nominalSpacing);
    for (let j = 0; j < intervals.length; j += 1) {
      const interval = intervals[j];
      const a = [interval.uMin * dirX + ln.v * nrmX, interval.uMin * dirZ + ln.v * nrmZ];
      const b = [interval.uMax * dirX + ln.v * nrmX, interval.uMax * dirZ + ln.v * nrmZ];
      const seg = ((segments.length % 2) === 0) ? { start: a, end: b } : { start: b, end: a };
      seg.kind = ln.kind || "main";
      seg.v = ln.v;
      segments.push(seg);
    }
  }
  return segments;
}

function getValidSurveyIntervalsAlongLine(uMin, uMax, v, dirX, dirZ, nrmX, nrmZ, nominalSpacing) {
  const span = Math.max(1, uMax - uMin);
  const sampleStep = Math.max(2, Math.min(12, Number(nominalSpacing) * 0.2 || 4));
  const sampleCount = Math.max(24, Math.ceil(span / sampleStep));
  const intervals = [];
  let startU = null;
  let lastValidU = null;

  for (let i = 0; i <= sampleCount; i += 1) {
    const t = i / Math.max(1, sampleCount);
    const u = uMin + (uMax - uMin) * t;
    const x = u * dirX + v * nrmX;
    const z = u * dirZ + v * nrmZ;
    const depth = sampleSurveyDepthAt(x, z);
    if (Number.isFinite(depth)) {
      if (startU == null) {
        startU = u;
      }
      lastValidU = u;
      continue;
    }
    if (startU != null && lastValidU != null && (lastValidU - startU) >= Math.max(8, sampleStep * 2)) {
      intervals.push(expandSurveyInterval(startU, lastValidU, v, dirX, dirZ, nrmX, nrmZ, nominalSpacing));
    }
    startU = null;
    lastValidU = null;
  }

  if (startU != null && lastValidU != null && (lastValidU - startU) >= Math.max(8, sampleStep * 2)) {
    intervals.push(expandSurveyInterval(startU, lastValidU, v, dirX, dirZ, nrmX, nrmZ, nominalSpacing));
  }
  return intervals;
}

function expandSurveyInterval(uMin, uMax, v, dirX, dirZ, nrmX, nrmZ, nominalSpacing) {
  const minRunout = Math.max(8, Number(nominalSpacing) * 0.9 || 8);
  const maxRunout = Math.max(minRunout, Math.max(12, Number(stateA.range) || 12) * 1.15);
  const startRunout = estimateLineRunoutAtU(uMin, v, dirX, dirZ, nrmX, nrmZ, nominalSpacing, minRunout, maxRunout);
  const endRunout = estimateLineRunoutAtU(uMax, v, dirX, dirZ, nrmX, nrmZ, nominalSpacing, minRunout, maxRunout);
  return {
    uMin: uMin - startRunout,
    uMax: uMax + endRunout,
  };
}

function estimateLineRunoutAtU(u, v, dirX, dirZ, nrmX, nrmZ, nominalSpacing, minRunout, maxRunout) {
  const depthSamples = [];
  const alongStep = Math.max(2, Math.min(10, Number(nominalSpacing) * 0.2 || 4));
  for (let offset = 0; offset <= 3; offset += 1) {
    const uu = u + (offset * alongStep);
    const xF = uu * dirX + v * nrmX;
    const zF = uu * dirZ + v * nrmZ;
    const dF = sampleSurveyDepthAt(xF, zF);
    if (Number.isFinite(dF)) depthSamples.push(dF);
    if (offset > 0) {
      const ub = u - (offset * alongStep);
      const xB = ub * dirX + v * nrmX;
      const zB = ub * dirZ + v * nrmZ;
      const dB = sampleSurveyDepthAt(xB, zB);
      if (Number.isFinite(dB)) depthSamples.push(dB);
    }
  }
  if (!depthSamples.length) {
    return minRunout;
  }
  depthSamples.sort((a, b) => a - b);
  const depth = depthSamples[Math.max(0, Math.floor(depthSamples.length * 0.5))] || depthSamples[0];
  const beamAngle = Math.max(1, Number(stateA.beamAngle) || 120);
  const halfSwath = Math.max(1, depth * Math.tan((beamAngle * Math.PI) / 360));
  return Math.max(minRunout, Math.min(maxRunout, halfSwath * 1.02));
}

function assessCoverageForSegments(samples, segments) {
  let uncovered = 0;
  const uncoveredSamples = [];
  for (let i = 0; i < samples.length; i += 1) {
    const p = samples[i];
    const halfSwathSq = p.halfSwath * p.halfSwath;
    let covered = false;
    for (let s = 0; s < segments.length; s += 1) {
      const seg = segments[s];
      const d2 = pointToSegmentDistSq(p.x, p.z, seg.start[0], seg.start[1], seg.end[0], seg.end[1]);
      if (d2 <= halfSwathSq) {
        covered = true;
        break;
      }
    }
    if (!covered) {
      uncovered += 1;
      uncoveredSamples.push(p);
    }
  }
  return {
    uncoveredPct: (uncovered / Math.max(1, samples.length)) * 100,
    uncoveredSamples,
  };
}

function buildInfillLineDefsFromCoverage(uncoveredSamples, baseLineDefs, minU, maxU, minV, maxV, nominalSpacing, maxSpacing) {
  if (!Array.isArray(uncoveredSamples) || uncoveredSamples.length < 8) {
    return [];
  }
  const tileU = Math.max(6, nominalSpacing * 0.9);
  const tileV = Math.max(4, nominalSpacing * 0.65);
  const uCount = Math.max(1, Math.ceil((maxU - minU) / tileU));
  const vCount = Math.max(1, Math.ceil((maxV - minV) / tileV));
  const cells = new Map();
  for (let i = 0; i < uncoveredSamples.length; i += 1) {
    const s = uncoveredSamples[i];
    const iu = Math.max(0, Math.min(uCount - 1, Math.floor((s.u - minU) / tileU)));
    const iv = Math.max(0, Math.min(vCount - 1, Math.floor((s.v - minV) / tileV)));
    const key = `${iu},${iv}`;
    if (!cells.has(key)) {
      cells.set(key, { iu, iv, count: 0, sumV: 0, minU: Infinity, maxU: -Infinity });
    }
    const c = cells.get(key);
    c.count += 1;
    c.sumV += s.v;
    c.minU = Math.min(c.minU, s.u);
    c.maxU = Math.max(c.maxU, s.u);
  }
  const entries = Array.from(cells.values());
  if (!entries.length) {
    return [];
  }
  const meanCellCount = entries.reduce((acc, e) => acc + e.count, 0) / Math.max(1, entries.length);
  const activeThreshold = Math.max(1, Math.floor(meanCellCount * 0.4));
  const active = new Map();
  for (let i = 0; i < entries.length; i += 1) {
    const e = entries[i];
    if (e.count >= activeThreshold) {
      active.set(`${e.iu},${e.iv}`, e);
    }
  }
  if (!active.size) {
    return [];
  }

  const clusters = [];
  const visited = new Set();
  const neighborOffsets = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ];
  for (const [key, cell] of active.entries()) {
    if (visited.has(key)) {
      continue;
    }
    const queue = [cell];
    visited.add(key);
    const cluster = { count: 0, sumV: 0, minU: Infinity, maxU: -Infinity };
    while (queue.length) {
      const cur = queue.pop();
      cluster.count += cur.count;
      cluster.sumV += cur.sumV;
      cluster.minU = Math.min(cluster.minU, cur.minU);
      cluster.maxU = Math.max(cluster.maxU, cur.maxU);
      for (let n = 0; n < neighborOffsets.length; n += 1) {
        const nu = cur.iu + neighborOffsets[n][0];
        const nv = cur.iv + neighborOffsets[n][1];
        const nKey = `${nu},${nv}`;
        if (!active.has(nKey) || visited.has(nKey)) {
          continue;
        }
        visited.add(nKey);
        queue.push(active.get(nKey));
      }
    }
    clusters.push(cluster);
  }
  if (!clusters.length) {
    return [];
  }
  const meanClusterCount = clusters.reduce((acc, c) => acc + c.count, 0) / Math.max(1, clusters.length);
  const minClusterCount = Math.max(6, Math.floor(meanClusterCount * 0.3));
  const existingV = baseLineDefs.map((l) => l.v);
  const infills = [];
  for (let i = 0; i < clusters.length; i += 1) {
    const m = clusters[i];
    if (m.count < minClusterCount) {
      continue;
    }
    const v = m.sumV / Math.max(1, m.count);
    let tooClose = false;
    for (let j = 0; j < existingV.length; j += 1) {
      if (Math.abs(existingV[j] - v) < nominalSpacing * 0.4) {
        tooClose = true;
        break;
      }
    }
    if (tooClose) {
      continue;
    }
    const margin = Math.max(8, maxSpacing * 0.45);
    const uMin = Math.max(minU, m.minU - margin);
    const uMax = Math.min(maxU, m.maxU + margin);
    if ((uMax - uMin) < Math.max(12, nominalSpacing * 0.5)) {
      continue;
    }
    infills.push({ v, uMin, uMax, kind: "infill" });
    existingV.push(v);
  }
  if (!infills.length && uncoveredSamples.length >= 4) {
    const sortedV = uncoveredSamples.map((s) => s.v).sort((a, b) => a - b);
    const sortedU = uncoveredSamples.map((s) => s.u).sort((a, b) => a - b);
    const centerV = sortedV[Math.floor(sortedV.length * 0.5)] ?? ((minV + maxV) * 0.5);
    const p10u = sortedU[Math.floor(sortedU.length * 0.1)] ?? minU;
    const p90u = sortedU[Math.floor(sortedU.length * 0.9)] ?? maxU;
    let tooClose = false;
    for (let j = 0; j < existingV.length; j += 1) {
      if (Math.abs(existingV[j] - centerV) < nominalSpacing * 0.35) {
        tooClose = true;
        break;
      }
    }
    if (!tooClose) {
      const margin = Math.max(6, nominalSpacing * 0.35);
      const uMinFallback = Math.max(minU, p10u - margin);
      const uMaxFallback = Math.min(maxU, p90u + margin);
      if ((uMaxFallback - uMinFallback) > Math.max(10, nominalSpacing * 0.4)) {
        infills.push({ v: centerV, uMin: uMinFallback, uMax: uMaxFallback, kind: "infill" });
      }
    }
  }
  infills.sort((a, b) => b.uMax - b.uMin - (a.uMax - a.uMin));
  return infills.slice(0, 10);
}

function estimateAdaptiveSpacingAtV(minU, maxU, v, dirX, dirZ, nrmX, nrmZ, overlapPct, maxSpacing) {
  const count = 64;
  const depths = [];
  const halfBand = Math.max(1, maxSpacing * 0.45);
  for (let i = 0; i < count; i += 1) {
    const t = i / Math.max(1, count - 1);
    const u = minU + (maxU - minU) * t;
    for (let s = -1; s <= 1; s += 1) {
      const vv = v + s * halfBand * 0.5;
      const x = u * dirX + vv * nrmX;
      const z = u * dirZ + vv * nrmZ;
      const depth = sampleSurveyDepthAt(x, z);
      if (Number.isFinite(depth)) {
        depths.push(depth);
      }
    }
  }
  if (!depths.length) {
    return maxSpacing;
  }
  depths.sort((a, b) => a - b);
  const q = depths[Math.max(0, Math.floor(depths.length * 0.12) - 1)] || depths[0];
  const beamAngle = Math.max(1, Number(stateA.beamAngle) || 120);
  const swath = 2 * q * Math.tan((beamAngle * Math.PI) / 360);
  const overlap = Math.max(0, Math.min(80, Number(overlapPct) || 0));
  const spacing = swath * (1 - overlap / 100) * 0.88;
  return Math.max(2, Math.min(maxSpacing, spacing));
}

function buildMissionWaypointsFromSegments(segments, skipTurns, spacing) {
  if (!Array.isArray(segments) || segments.length === 0) {
    return [];
  }
  const points = [];
  const eps2 = 1e-8;
  const pushPoint = (x, z, mode = "line", force = false) => {
    if (!points.length || force) {
      points.push({ x, z, mode });
      return;
    }
    const last = points[points.length - 1];
    const dx = x - last.x;
    const dz = z - last.z;
    if ((dx * dx + dz * dz) > eps2 || mode !== last.mode) {
      points.push({ x, z, mode });
    }
  };

  pushPoint(segments[0].start[0], segments[0].start[1], "line", true);
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i];
    pushPoint(seg.end[0], seg.end[1], "line");
    if (i >= segments.length - 1) {
      continue;
    }
    const nextSeg = segments[i + 1];
    const nextStart = nextSeg.start;
    if (skipTurns) {
      pushPoint(nextStart[0], nextStart[1], "teleport", true);
      continue;
    }
    const arcPoints = buildMissionTurnArc(seg, nextStart, spacing);
    for (let j = 0; j < arcPoints.length; j += 1) {
      pushPoint(arcPoints[j][0], arcPoints[j][1], "turn");
    }
    pushPoint(nextStart[0], nextStart[1], "line");
  }
  return points;
}

function optimizeMissionHeading(ext, overlap, seedHeading) {
  const samples = buildCoverageSamples(ext, 2400);
  if (!samples.length) {
    return { heading: seedHeading, uncoveredPct: 100, lines: 0 };
  }
  const coarse = [];
  for (let h = -90; h < 90; h += 10) {
    coarse.push(h);
  }
  let best = null;
  for (let i = 0; i < coarse.length; i += 1) {
    const candidate = evaluateHeadingCandidate(ext, overlap, coarse[i], samples);
    if (!best || candidate.score < best.score) {
      best = candidate;
    }
  }
  const fineHeadings = [];
  const center = canonicalHeading180(best?.heading ?? seedHeading);
  for (let h = center - 10; h <= center + 10 + 1e-6; h += 2) {
    fineHeadings.push(canonicalHeading180(h));
  }
  for (let i = 0; i < fineHeadings.length; i += 1) {
    const candidate = evaluateHeadingCandidate(ext, overlap, fineHeadings[i], samples);
    if (!best || candidate.score < best.score) {
      best = candidate;
    }
  }
  const heading = denormalizeHeading180(best?.heading ?? seedHeading, seedHeading);
  return {
    heading,
    uncoveredPct: best?.uncoveredPct ?? 100,
    lines: best?.lines ?? 0,
  };
}

function evaluateHeadingCandidate(ext, overlap, heading, samples) {
  const spacing = estimateRepresentativeLineSpacing(ext, overlap);
  const generated = buildLinePlanFromExtents(ext, heading, spacing, overlap, { includeInfill: false });
  const segments = generated.segments;
  if (!segments.length) {
    return { heading, score: Number.POSITIVE_INFINITY, uncoveredPct: 100, lines: 0 };
  }
  const coverage = assessCoverageForSegments(samples, segments);
  const uncoveredPct = coverage.uncoveredPct;
  const spanX = Math.max(1, Number(ext.maxX) - Number(ext.minX));
  const spanZ = Math.max(1, Number(ext.maxZ) - Number(ext.minZ));
  const diag = Math.hypot(spanX, spanZ);
  const lengthPenalty = generated.totalLength / Math.max(1, diag);
  const score = uncoveredPct * 100 + lengthPenalty;
  return {
    heading: canonicalHeading180(heading),
    score,
    uncoveredPct,
    lines: segments.length,
  };
}

function estimateRepresentativeLineSpacing(ext, overlapPct) {
  const samples = buildCoverageSamples(ext, 1200);
  if (!samples.length) {
    return Math.max(4, Number(stateA.range) || 4);
  }
  const widths = [];
  for (let i = 0; i < samples.length; i += 1) {
    widths.push(samples[i].halfSwath * 2);
  }
  widths.sort((a, b) => a - b);
  const qIndex = Math.max(0, Math.floor(widths.length * 0.1) - 1);
  const representativeWidth = widths[qIndex] || widths[0] || Math.max(4, Number(stateA.range) || 4);
  const overlap = Math.max(0, Math.min(80, Number(overlapPct) || 0));
  return Math.max(4, representativeWidth * (1 - overlap / 100) * 0.88);
}

function buildCoverageSamples(ext, targetCount, axes = null) {
  const minX = Number(ext.minX) || -1400;
  const maxX = Number(ext.maxX) || 1400;
  const minZ = Number(ext.minZ) || -1400;
  const maxZ = Number(ext.maxZ) || 1400;
  const spanX = Math.max(1, maxX - minX);
  const spanZ = Math.max(1, maxZ - minZ);
  const aspect = spanX / spanZ;
  const nx = Math.max(8, Math.round(Math.sqrt(targetCount * aspect)));
  const nz = Math.max(8, Math.round(targetCount / nx));
  const beamHalfTan = Math.tan((Math.max(1, Number(stateA.beamAngle) || 1) * Math.PI) / 360);
  const maxDepth = Math.max(1, Number(stateA.range) || 1);
  const samples = [];
  for (let iz = 0; iz < nz; iz += 1) {
    const z = minZ + ((iz + 0.5) / nz) * spanZ;
    for (let ix = 0; ix < nx; ix += 1) {
      const x = minX + ((ix + 0.5) / nx) * spanX;
      const depth = sampleSurveyDepthAt(x, z);
      if (!Number.isFinite(depth)) {
        continue;
      }
      const sample = { x, z, halfSwath: Math.max(1, Math.min(maxDepth, depth) * beamHalfTan) };
      if (axes) {
        sample.u = x * axes.dirX + z * axes.dirZ;
        sample.v = x * axes.nrmX + z * axes.nrmZ;
      }
      samples.push(sample);
    }
  }
  return samples;
}

function pointToSegmentDistSq(px, pz, ax, az, bx, bz) {
  const abx = bx - ax;
  const abz = bz - az;
  const apx = px - ax;
  const apz = pz - az;
  const ab2 = abx * abx + abz * abz;
  if (ab2 <= 1e-12) {
    return apx * apx + apz * apz;
  }
  const t = Math.max(0, Math.min(1, (apx * abx + apz * abz) / ab2));
  const qx = ax + abx * t;
  const qz = az + abz * t;
  const dx = px - qx;
  const dz = pz - qz;
  return dx * dx + dz * dz;
}

function canonicalHeading180(heading) {
  let h = Number(heading) || 0;
  while (h >= 90) h -= 180;
  while (h < -90) h += 180;
  return h;
}

function denormalizeHeading180(canonical, reference) {
  const ref = Number(reference) || 0;
  const c = canonicalHeading180(canonical);
  const candidates = [c - 180, c, c + 180];
  let best = candidates[0];
  let bestErr = Math.abs(candidates[0] - ref);
  for (let i = 1; i < candidates.length; i += 1) {
    const err = Math.abs(candidates[i] - ref);
    if (err < bestErr) {
      best = candidates[i];
      bestErr = err;
    }
  }
  let out = best;
  while (out > 180) out -= 360;
  while (out < -180) out += 360;
  return out;
}

function buildMissionTurnArc(seg, nextStart, spacing) {
  const p0 = seg.end;
  const p1 = nextStart;
  const dx = p1[0] - p0[0];
  const dz = p1[1] - p0[1];
  const d = Math.hypot(dx, dz);
  if (d < 1e-6) {
    return [];
  }
  const cx = (p0[0] + p1[0]) * 0.5;
  const cz = (p0[1] + p1[1]) * 0.5;
  const r = d * 0.5;
  const theta0 = Math.atan2(p0[1] - cz, p0[0] - cx);
  const ccwTangent = [-(p0[1] - cz) / r, (p0[0] - cx) / r];
  const lineDx = seg.end[0] - seg.start[0];
  const lineDz = seg.end[1] - seg.start[1];
  const lineLen = Math.hypot(lineDx, lineDz) || 1;
  const lineDir = [lineDx / lineLen, lineDz / lineLen];
  const dotCcw = ccwTangent[0] * lineDir[0] + ccwTangent[1] * lineDir[1];
  const dotCw = (-ccwTangent[0]) * lineDir[0] + (-ccwTangent[1]) * lineDir[1];
  const sign = dotCcw >= dotCw ? 1 : -1;
  const stepHint = Math.max(4, Math.min(16, Math.round((Math.PI * Math.max(r, spacing * 0.5)) / 18)));
  const arc = [];
  for (let k = 1; k < stepHint; k += 1) {
    const t = k / stepHint;
    const th = theta0 + sign * Math.PI * t;
    arc.push([cx + Math.cos(th) * r, cz + Math.sin(th) * r]);
  }
  return arc;
}

function drawAttitudeGraph() {
  if (!attitudeCtx || !attitudeCanvas) {
    return;
  }

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssW = attitudeCanvas.clientWidth || 210;
  const cssH = attitudeCanvas.clientHeight || 92;
  const pxW = Math.max(1, Math.floor(cssW * dpr));
  const pxH = Math.max(1, Math.floor(cssH * dpr));
  if (attitudeCanvas.width !== pxW || attitudeCanvas.height !== pxH) {
    attitudeCanvas.width = pxW;
    attitudeCanvas.height = pxH;
  }

  const ctx = attitudeCtx;
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  ctx.fillStyle = "rgba(4,10,18,0.9)";
  ctx.fillRect(0, 0, cssW, cssH);

  const padLeft = 30;
  const padRight = 4;
  const padTop = 8;
  const padBottom = 4;
  const plotW = Math.max(1, cssW - padLeft - padRight);
  const plotH = Math.max(1, cssH - padTop - padBottom);

  ctx.strokeStyle = "rgba(90,130,170,0.26)";
  ctx.lineWidth = 1;
  for (let i = 1; i < 4; i += 1) {
    const y = padTop + (plotH * i) / 4;
    ctx.beginPath();
    ctx.moveTo(padLeft, y);
    ctx.lineTo(padLeft + plotW, y);
    ctx.stroke();
  }

  ctx.fillStyle = "rgba(163,190,216,0.9)";
  ctx.font = "10px sans-serif";
  ctx.textAlign = "right";
  ctx.textBaseline = "top";

  if (!attitudeSamples.length) {
    ctx.fillText("5", padLeft - 5, padTop);
    ctx.textBaseline = "middle";
    ctx.fillText("0", padLeft - 5, padTop + plotH * 0.5);
    ctx.textBaseline = "bottom";
    ctx.fillText("-5", padLeft - 5, padTop + plotH);
    ctx.restore();
    return;
  }

  let maxAbs = 5;
  for (let i = 0; i < attitudeSamples.length; i += 1) {
    const s = attitudeSamples[i];
    maxAbs = Math.max(maxAbs, Math.abs(s.roll), Math.abs(s.pitch), Math.abs(s.yaw));
  }
  const scale = maxAbs * 1.12;
  drawSeries(ctx, attitudeSamples, padLeft, padTop, plotW, plotH, scale, "roll", "#7cc6ff");
  drawSeries(ctx, attitudeSamples, padLeft, padTop, plotW, plotH, scale, "pitch", "#94efb6");
  drawSeries(ctx, attitudeSamples, padLeft, padTop, plotW, plotH, scale, "yaw", "#ffcf7c");
  ctx.fillText(`${scale.toFixed(0)}`, padLeft - 5, padTop);
  ctx.textBaseline = "middle";
  ctx.fillText("0", padLeft - 5, padTop + plotH * 0.5);
  ctx.textBaseline = "bottom";
  ctx.fillText(`${(-scale).toFixed(0)}`, padLeft - 5, padTop + plotH);
  ctx.restore();
}

function drawSeries(ctx, samples, x0, y0, w, h, scale, key, strokeStyle) {
  const n = samples.length;
  if (n < 2) {
    return;
  }
  ctx.strokeStyle = strokeStyle;
  ctx.lineWidth = 1.35;
  ctx.beginPath();
  for (let i = 0; i < n; i += 1) {
    const x = x0 + (i / (n - 1)) * w;
    const y = y0 + h * 0.5 - (samples[i][key] / scale) * (h * 0.42);
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();
}

function drawNadirGraph() {
  if (!nadirCtx || !nadirCanvas) {
    return;
  }
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssW = nadirCanvas.clientWidth || 210;
  const cssH = nadirCanvas.clientHeight || 68;
  const pxW = Math.max(1, Math.floor(cssW * dpr));
  const pxH = Math.max(1, Math.floor(cssH * dpr));
  if (nadirCanvas.width !== pxW || nadirCanvas.height !== pxH) {
    nadirCanvas.width = pxW;
    nadirCanvas.height = pxH;
  }

  const ctx = nadirCtx;
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);
  ctx.fillStyle = "rgba(4,10,18,0.9)";
  ctx.fillRect(0, 0, cssW, cssH);

  const padLeft = 34;
  const padRight = 4;
  const padTop = 8;
  const padBottom = 4;
  const plotW = Math.max(1, cssW - padLeft - padRight);
  const plotH = Math.max(1, cssH - padTop - padBottom);

  ctx.strokeStyle = "rgba(90,130,170,0.26)";
  ctx.lineWidth = 1;
  const midY = padTop + plotH * 0.5;
  ctx.beginPath();
  ctx.moveTo(padLeft, midY);
  ctx.lineTo(padLeft + plotW, midY);
  ctx.stroke();

  ctx.fillStyle = "rgba(163,190,216,0.9)";
  ctx.font = "10px sans-serif";
  ctx.textAlign = "right";
  ctx.textBaseline = "top";

  const n = nadirDepthSamples.length;
  if (n < 2) {
    ctx.textBaseline = "bottom";
    ctx.fillText("0", padLeft - 5, padTop + plotH);
    ctx.restore();
    return;
  }

  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < n; i += 1) {
    min = Math.min(min, nadirDepthSamples[i]);
    max = Math.max(max, nadirDepthSamples[i]);
  }
  const range = Math.max(0.5, max - min);
  const pad = range * 0.12;
  const yMin = min - pad;
  const yMax = max + pad;
  const yMid = (yMin + yMax) * 0.5;

  ctx.strokeStyle = "#8fd8ff";
  ctx.lineWidth = 1.35;
  ctx.beginPath();
  for (let i = 0; i < n; i += 1) {
    const x = padLeft + (i / (n - 1)) * plotW;
    const t = (nadirDepthSamples[i] - yMin) / Math.max(1e-6, yMax - yMin);
    const y = padTop + plotH - t * plotH;
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();
  ctx.fillText(formatGraphNumber(yMax), padLeft - 5, padTop);
  ctx.textBaseline = "middle";
  ctx.fillText(formatGraphNumber(yMid), padLeft - 5, padTop + plotH * 0.5);
  ctx.textBaseline = "bottom";
  ctx.fillText(formatGraphNumber(yMin), padLeft - 5, padTop + plotH);
  ctx.restore();
}

function drawRawStripGraph() {
  if (!rawStripCtx || !rawStripCanvas) {
    return;
  }
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssW = rawStripCanvas.clientWidth || 210;
  const cssH = rawStripCanvas.clientHeight || 92;
  const pxW = Math.max(1, Math.floor(cssW * dpr));
  const pxH = Math.max(1, Math.floor(cssH * dpr));
  if (rawStripCanvas.width !== pxW || rawStripCanvas.height !== pxH) {
    rawStripCanvas.width = pxW;
    rawStripCanvas.height = pxH;
  }

  const ctx = rawStripCtx;
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);
  ctx.fillStyle = "rgba(4,10,18,0.9)";
  ctx.fillRect(0, 0, cssW, cssH);

  const padLeft = 28;
  const padRight = 6;
  const padTop = 8;
  const padBottom = 14;
  const plotW = Math.max(1, cssW - padLeft - padRight);
  const plotH = Math.max(1, cssH - padTop - padBottom);

  ctx.strokeStyle = "rgba(90,130,170,0.26)";
  ctx.lineWidth = 1;
  for (let i = 0; i <= 2; i += 1) {
    const y = padTop + (i / 2) * plotH;
    ctx.beginPath();
    ctx.moveTo(padLeft, y);
    ctx.lineTo(padLeft + plotW, y);
    ctx.stroke();
  }
  const midX = padLeft + plotW * 0.5;
  ctx.beginPath();
  ctx.moveTo(midX, padTop);
  ctx.lineTo(midX, padTop + plotH);
  ctx.stroke();

  const hits = rawStripSnapshot?.hits;
  if (!Array.isArray(hits) || hits.length < 2) {
    ctx.fillStyle = "rgba(163,190,216,0.9)";
    ctx.font = "10px sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("Awaiting pings...", padLeft + 4, padTop + 4);
    ctx.restore();
    return;
  }

  const beamHalfTan = Math.tan((Math.max(1, Number(stateA.beamAngle) || 1) * Math.PI) / 360);
  const halfSwath = Math.max(1, (Number(stateA.range) || 1) * beamHalfTan);
  const points = [];
  for (let i = 0; i < hits.length; i += 1) {
    const h = hits[i];
    const u = Number(h?.u);
    const y = Number(h?.point?.[1]);
    if (!Number.isFinite(u) || !Number.isFinite(y)) continue;
    points.push({ x: u * halfSwath, depth: Math.max(0, -y) });
  }
  if (points.length < 2) {
    ctx.restore();
    return;
  }
  points.sort((a, b) => a.x - b.x);
  let minX = points[0].x;
  let maxX = points[points.length - 1].x;
  if (Math.abs(maxX - minX) < 1e-6) {
    minX -= 1;
    maxX += 1;
  }

  const yMin = 0;
  const yMax = Math.max(1, Number(stateA.range) || 1);
  const xMin = minX;
  const xMax = maxX;
  const toPlotX = (x) => padLeft + ((x - xMin) / Math.max(1e-6, xMax - xMin)) * plotW;
  const toPlotY = (depth) => {
    const clamped = Math.max(yMin, Math.min(yMax, depth));
    return padTop + ((clamped - yMin) / Math.max(1e-6, yMax - yMin)) * plotH;
  };

  ctx.strokeStyle = "#ffd89d";
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  for (let i = 0; i < points.length; i += 1) {
    const px = toPlotX(points[i].x);
    const py = toPlotY(points[i].depth);
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.stroke();

  ctx.fillStyle = "#ffd89d";
  for (let i = 0; i < points.length; i += 1) {
    const px = toPlotX(points[i].x);
    const py = toPlotY(points[i].depth);
    ctx.fillRect(px - 1, py - 1, 2, 2);
  }

  ctx.fillStyle = "rgba(163,190,216,0.9)";
  ctx.font = "10px sans-serif";
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.fillText(formatGraphNumber(yMin), padLeft - 5, padTop);
  ctx.textBaseline = "middle";
  ctx.fillText(formatGraphNumber((yMin + yMax) * 0.5), padLeft - 5, padTop + plotH * 0.5);
  ctx.textBaseline = "bottom";
  ctx.fillText(formatGraphNumber(yMax), padLeft - 5, padTop + plotH);
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("port", padLeft + 1, padTop + plotH + 2);
  ctx.textAlign = "center";
  ctx.fillText("nadir", padLeft + plotW * 0.5, padTop + plotH + 2);
  ctx.textAlign = "right";
  ctx.fillText("stbd", padLeft + plotW, padTop + plotH + 2);
  ctx.restore();
}

function drawDensityQualityGraph() {
  if (!densityQualityCtx || !densityQualityCanvas) {
    return;
  }
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssW = densityQualityCanvas.clientWidth || 210;
  const cssH = densityQualityCanvas.clientHeight || 68;
  const pxW = Math.max(1, Math.floor(cssW * dpr));
  const pxH = Math.max(1, Math.floor(cssH * dpr));
  if (densityQualityCanvas.width !== pxW || densityQualityCanvas.height !== pxH) {
    densityQualityCanvas.width = pxW;
    densityQualityCanvas.height = pxH;
  }

  const ctx = densityQualityCtx;
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);
  ctx.fillStyle = "rgba(4,10,18,0.9)";
  ctx.fillRect(0, 0, cssW, cssH);

  const padLeft = 34;
  const padRight = 4;
  const padTop = 8;
  const padBottom = 4;
  const plotW = Math.max(1, cssW - padLeft - padRight);
  const plotH = Math.max(1, cssH - padTop - padBottom);

  ctx.strokeStyle = "rgba(90,130,170,0.26)";
  ctx.lineWidth = 1;
  for (let i = 0; i <= 2; i += 1) {
    const y = padTop + (i / 2) * plotH;
    ctx.beginPath();
    ctx.moveTo(padLeft, y);
    ctx.lineTo(padLeft + plotW, y);
    ctx.stroke();
  }

  ctx.fillStyle = "rgba(163,190,216,0.9)";
  ctx.font = "10px sans-serif";
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.fillText("100", padLeft - 5, padTop);
  ctx.textBaseline = "middle";
  ctx.fillText("50", padLeft - 5, padTop + plotH * 0.5);
  ctx.textBaseline = "bottom";
  ctx.fillText("0", padLeft - 5, padTop + plotH);

  const n = densityQualitySamples.length;
  if (n < 2) {
    ctx.restore();
    return;
  }

  ctx.strokeStyle = "#9be2ae";
  ctx.lineWidth = 1.35;
  ctx.beginPath();
  for (let i = 0; i < n; i += 1) {
    const x = padLeft + (i / (n - 1)) * plotW;
    const pct = Math.max(0, Math.min(100, Number(densityQualitySamples[i]) || 0));
    const y = padTop + plotH - (pct / 100) * plotH;
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();
  ctx.restore();
}

function drawDensityHistogramGraph() {
  if (!densityHistogramCtx || !densityHistogramCanvas) {
    return;
  }
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const cssW = densityHistogramCanvas.clientWidth || 210;
  const cssH = densityHistogramCanvas.clientHeight || 88;
  const pxW = Math.max(1, Math.floor(cssW * dpr));
  const pxH = Math.max(1, Math.floor(cssH * dpr));
  if (densityHistogramCanvas.width !== pxW || densityHistogramCanvas.height !== pxH) {
    densityHistogramCanvas.width = pxW;
    densityHistogramCanvas.height = pxH;
  }

  const ctx = densityHistogramCtx;
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);
  const bgGradient = ctx.createLinearGradient(0, 0, 0, cssH);
  bgGradient.addColorStop(0, "rgba(23,8,31,0.95)");
  bgGradient.addColorStop(0.6, "rgba(14,6,23,0.93)");
  bgGradient.addColorStop(1, "rgba(7,4,15,0.95)");
  ctx.fillStyle = bgGradient;
  ctx.fillRect(0, 0, cssW, cssH);

  const padLeft = 34;
  const padRight = 4;
  const padTop = 8;
  const padBottom = 14;
  const plotW = Math.max(1, cssW - padLeft - padRight);
  const plotH = Math.max(1, cssH - padTop - padBottom);

  ctx.strokeStyle = "rgba(228,110,255,0.24)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(padLeft, padTop + plotH);
  ctx.lineTo(padLeft + plotW, padTop + plotH);
  ctx.stroke();

  const touched = Math.max(0, densityState.cellsHit);
  if (touched <= 0 || densityState.countHistogram.size === 0) {
  ctx.fillStyle = "rgba(163,190,216,0.9)";
  ctx.font = "10px sans-serif";
  ctx.textAlign = "right";
  ctx.textBaseline = "bottom";
  ctx.fillText("0%", padLeft - 5, padTop + plotH);
  ctx.restore();
  return;
  }

  const maxHits = Math.max(1, densityState.maxCount);
  const targetBins = Math.ceil(Math.sqrt(maxHits) * 3);
  const binCount = Math.max(24, Math.min(72, targetBins));
  const binSize = Math.max(1, Math.ceil(maxHits / binCount));
  const bins = Array.from({ length: binCount }, () => 0);
  densityState.countHistogram.forEach((cellCount, hitsPerCell) => {
    const idx = Math.min(binCount - 1, Math.floor((hitsPerCell - 1) / binSize));
    bins[idx] += cellCount;
  });

  let maxPct = 0;
  for (let i = 0; i < bins.length; i += 1) {
    const pct = (bins[i] / touched) * 100;
    if (pct > maxPct) {
      maxPct = pct;
    }
  }
  maxPct = Math.max(2, maxPct);

  ctx.fillStyle = "rgba(255,183,255,0.92)";
  ctx.font = "10px sans-serif";
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.fillText(`${maxPct.toFixed(0)}%`, padLeft - 5, padTop);
  ctx.textBaseline = "bottom";
  ctx.fillText("0%", padLeft - 5, padTop + plotH);

  const barGap = 0;
  const barW = Math.max(1, Math.floor((plotW - barGap * (binCount - 1)) / binCount));
  const primaryTarget = getDensityPrimaryTarget();
  for (let i = 0; i < bins.length; i += 1) {
    const x0 = padLeft + i * (barW + barGap);
    const binStart = i * binSize + 1;
    const pct = (bins[i] / touched) * 100;
    const h = Math.max(0, (pct / maxPct) * plotH);
    const y0 = padTop + plotH - h;
    const meetsPrimary = binStart >= primaryTarget;
    const barGradient = ctx.createLinearGradient(0, y0, 0, y0 + Math.max(h, 1));
    if (meetsPrimary) {
      barGradient.addColorStop(0, "rgba(255,128,225,0.98)");
      barGradient.addColorStop(1, "rgba(219,72,186,0.86)");
    } else {
      barGradient.addColorStop(0, "rgba(211,95,255,0.95)");
      barGradient.addColorStop(1, "rgba(145,58,214,0.82)");
    }
    ctx.fillStyle = barGradient;
    ctx.shadowColor = meetsPrimary ? "rgba(255,130,223,0.62)" : "rgba(197,101,255,0.55)";
    ctx.shadowBlur = 7;
    ctx.fillRect(x0, y0, barW, h);
    ctx.shadowBlur = 0;
  }

  ctx.fillStyle = "rgba(255,183,255,0.9)";
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.fillText("1", padLeft, padTop + plotH + 2);
  ctx.textAlign = "right";
  ctx.fillText(`${maxHits}`, padLeft + plotW, padTop + plotH + 2);
  ctx.restore();
}

function appendDensityQualitySample(report = null) {
  const pct = report && Number.isFinite(Number(report.primaryCompliance))
    ? Number(report.primaryCompliance)
    : (() => {
      const hit = Math.max(0, densityState.cellsHit);
      const total = Math.max(0, densityState.totalCells);
      const basis = getDensityQualityBasis();
      const target = getDensityPrimaryTarget();
      const meeting = getDensityMeetingCells(target);
      const denominator = basis === "aoi" ? total : hit;
      return denominator > 0 ? (meeting / denominator) * 100 : 0;
    })();
  densityQualitySamples.push(Math.max(0, Math.min(100, pct)));
  if (densityQualitySamples.length > maxAttitudeSamples) {
    densityQualitySamples.splice(0, densityQualitySamples.length - maxAttitudeSamples);
  }
}

function formatGraphNumber(value) {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 100) {
    return `${Math.round(n)}`;
  }
  if (Math.abs(n) >= 10) {
    return `${n.toFixed(1)}`;
  }
  return `${n.toFixed(2)}`;
}
